'use client';

import type { EmblaCarouselType } from 'embla-carousel';
import { animate, type AnimationPlaybackControlsWithThen } from 'motion/react';
import { type UIEvent, useEffect, useRef, useState } from 'react';
import { SLIDE_PROGRESS_CSS_VAR } from '@/app/components/navigation/pagination/pagination-dash/constants';
import { usePaginationSlider } from '@/app/components/sliders/hooks/usePaginationSlider';
import { playVideoWithPromiseHandle } from '@/app/utils/video';
const IMAGE_DURATION = 6;
interface useMediaSliderProps {
  inView: boolean;
  items?: (string | undefined)[];
  imageDuration?: number;
  initialIsPaused?: boolean;
}
export const useMediaSlider = ({
  inView = false,
  items = [],
  imageDuration = IMAGE_DURATION,
  initialIsPaused = true
}: useMediaSliderProps) => {
  const playPromiseRef = useRef<Promise<void> | null>(null);
  const staticImageDashAnimationRef = useRef<AnimationPlaybackControlsWithThen | null>(null);
  const [emblaApi, setEmblaApi] = useState<EmblaCarouselType | null>(null);
  const autoplay = emblaApi?.plugins().autoplay;
  const {
    currentPage,
    onPageChange,
    scrollSnaps
  } = usePaginationSlider(emblaApi);
  const [isPaused, setIsPaused] = useState<boolean>(initialIsPaused);
  const [enableLoadVideo, setEnableLoadVideo] = useState<boolean>(false);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isVideoLoadeds, setIsVideoLoadeds] = useState<boolean[]>(items.map(_ => false));
  const loadedVideosRef = useRef<(HTMLVideoElement | undefined)[]>([]);
  const paginationItemsRefs = useRef<(HTMLDivElement | null)[]>([]);
  const rafRef = useRef<number | null>(null);
  const currentIndexRef = useRef<number>(0);
  const goNextSlide = () => {
    if (!emblaApi) return;
    emblaApi.canScrollNext() ? emblaApi.scrollNext() : emblaApi.scrollTo(0);
  };
  const stopProgressRaf = () => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };
  const onVideoPlay = (e: Event) => {
    setIsPaused(false);
    const videoEl = e.currentTarget as HTMLVideoElement;
    const index = loadedVideosRef.current.findIndex(v => v === videoEl);
    if (index < 0) return;
    stopProgressRaf();
    const loop = () => {
      const dashEl = paginationItemsRefs.current[index];
      const duration = videoEl.duration;
      if (dashEl && duration && Number.isFinite(duration)) {
        const progress = videoEl.currentTime / duration;
        dashEl.style.setProperty(SLIDE_PROGRESS_CSS_VAR, String(progress));
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
  };
  const onVideoPause = () => {
    setIsPaused(true);
    stopProgressRaf();
  };
  const safePlayVideo = async (index: number) => {
    const videoEl = loadedVideosRef.current[index];
    if (videoEl) {
      playPromiseRef.current = playVideoWithPromiseHandle(videoEl);
      await playPromiseRef.current;
    }
  };
  const safePauseVideo = async (index: number) => {
    const videoEl = loadedVideosRef.current[index];
    if (!videoEl) {
      return;
    }
    if (playPromiseRef.current) {
      // Wait for play to finish before pausing
      await playPromiseRef.current;
    }
    videoEl.pause();
  };
  const clearDashProgress = async (index: number) => {
    const dashEl = paginationItemsRefs.current[index];
    if (!dashEl) return;
    await animate(dashEl, {
      [SLIDE_PROGRESS_CSS_VAR]: 1
    }, {
      ease: 'easeOut'
    });
  };
  const onVideoEnded = async (index: number) => {
    await safePauseVideo(index);
    await clearDashProgress(index);
    if (index === currentIndexRef.current) {
      goNextSlide();
    }
  };
  const onPlayImage = () => {
    staticImageDashAnimationRef.current?.play();
    setIsPaused(false);
  };
  const onPauseImage = () => {
    staticImageDashAnimationRef.current?.pause();
    setIsPaused(true);
  };
  const onPlaySlide = async (index: number) => {
    const isImage = !items[index];
    if (isImage) {
      onPlayImage();
      return;
    }
    await safePlayVideo(index);
    autoplay?.play();
  };
  const onPauseSlide = async (index: number) => {
    const isImage = !items[index];
    if (isImage) {
      onPauseImage();
      return;
    }
    await safePauseVideo(index);
    autoplay?.stop();
  };
  const onTogglePlayPause = (index: number) => {
    if (index !== currentIndex) return;
    const isImage = !items[index];
    if (isImage) {
      staticImageDashAnimationRef.current?.state === 'paused' ? onPlayImage() : onPauseImage();
      return;
    }
    if (isPaused) {
      safePlayVideo(index);
    } else {
      safePauseVideo(index);
    }
  };
  const setupStaticImageDashAnimation = (index: number) => {
    const dashEl = paginationItemsRefs.current[index];
    if (!dashEl) return;
    staticImageDashAnimationRef.current = animate(dashEl, {
      [SLIDE_PROGRESS_CSS_VAR]: [0, 1]
    }, {
      duration: imageDuration,
      ease: 'linear',
      onComplete: () => {
        goNextSlide();
      }
    });
  };
  const removeSlideEvents = async (index: number) => {
    const videoEl = loadedVideosRef.current[index];
    if (videoEl) {
      await safePauseVideo(index);
      stopProgressRaf();
      videoEl.currentTime = 0;
      videoEl.removeEventListener('play', onVideoPlay);
      videoEl.removeEventListener('pause', onVideoPause);
      return;
    }
    if (staticImageDashAnimationRef.current) {
      staticImageDashAnimationRef.current.stop();
      staticImageDashAnimationRef.current = null;
    }
  };
  const onSlideChange = (index: number) => {
    if (index === currentIndex) return;
    removeSlideEvents(currentIndex); // Clean up old slide first
    currentIndexRef.current = index;
    setCurrentIndex(index);

    // Setup logic for the new slide only for image. For Video, it will be set up in onVideoReadyAndActive
    const isImage = !items[index];
    if (isImage) {
      setupStaticImageDashAnimation(index);
      setIsPaused(false); // Static image is always "playing"
    }
  };
  const onClickSlider = (index: number) => {
    if (emblaApi && index !== currentIndex) {
      emblaApi.scrollTo(index);
    }
  };

  // From loadedmetadata event
  const onVideoReadyAndActive = async (e: UIEvent<HTMLVideoElement> | undefined, index: number) => {
    const videoEl = e?.target as HTMLVideoElement;
    loadedVideosRef.current[index] = videoEl;
    setIsVideoLoadeds(prev => {
      const results = prev.slice();
      results[index] = true;
      return results;
    });
    if (!inView) return;
    if (videoEl) {
      /**
       * We use timeupdate to make sure that video is really playing. Because the 'play' event
       * fires when the play() method is called and the browser has begun the process of playback
       * but it doesn't guarantee instantaneous visual playback so this will make the progress animation more accurate
       */
      const onFirstTimeUpdate = (e: Event) => {
        videoEl.removeEventListener('timeupdate', onFirstTimeUpdate);
        onVideoPlay(e);
      };
      videoEl.addEventListener('timeupdate', onFirstTimeUpdate);
      videoEl.addEventListener('play', onVideoPlay);
      videoEl.addEventListener('pause', onVideoPause);
    }
    if (index === currentIndex) {
      await safePlayVideo(index);
    }
  };
  useEffect(() => {
    const nextIndex = currentPage - 1;
    if (!emblaApi || nextIndex < 0) return;
    onSlideChange(nextIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, emblaApi]);

  // Load/Play video optimization only inView
  useEffect(() => {
    const isImage = !items[currentIndex];
    if (!inView) {
      isImage ? onPauseImage() : safePauseVideo(currentIndex);
      return;
    }
    if (!enableLoadVideo) {
      setEnableLoadVideo(true);
    }
    if (isImage) {
      setupStaticImageDashAnimation(currentIndex);
      onPlayImage();
    } else {
      safePlayVideo(currentIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  // Cleanup for unmount
  useEffect(() => {
    return () => {
      removeSlideEvents(currentIndex);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return {
    emblaApi,
    setEmblaApi,
    isPaused,
    enableLoadVideo,
    currentIndex,
    loadedVideosRef,
    paginationItemsRefs,
    currentPage,
    scrollSnaps,
    isVideoLoadeds,
    onPageChange,
    onVideoEnded,
    onTogglePlayPause,
    onClickSlider,
    onVideoReadyAndActive,
    onPlaySlide,
    onPauseSlide
  };
};